// src/pages/Dashboard/DashboardLayout.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import ContactPhoneOutlinedIcon from "@mui/icons-material/ContactPhoneOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useAuth } from "../../context/AuthContext";
import MainDashboard from "./MainDashboard";
import "../../styles/dashboard.css";

const PATH_TO_TAB = {
  "/dashboard/micuenta": "micuenta",
  "/dashboard/gestionar-proyectos": "gestionar-proyectos",
  "/dashboard/proyectos": "misproyectos",
  "/dashboard/misproyectos": "misproyectos",
  "/dashboard/registro-proyecto": "crear-proyecto",
  "/dashboard/editar-proyecto": "gestionar-proyectos",
  "/dashboard/crear-proyecto": "crear-proyecto",
  "/dashboard/socios": "socios",
  "/dashboard/registro-socios": "socios",
  "/dashboard/noticias": "noticias",
  "/dashboard/nueva-noticia": "noticias",
  "/dashboard/editar-noticia": "noticias",
  "/dashboard/cursos": "cursos",
  "/dashboard/nuevo-curso": "cursos",
  "/dashboard/editar-curso": "cursos",
  "/dashboard/eventos": "eventos",
  "/dashboard/nuevo-evento": "eventos",
  "/dashboard/editar-evento": "eventos",
  "/dashboard/contacto": "contacto",
  "/dashboard/contactos-inbox": "contactos-inbox",
  "/dashboard/credenciales": "credenciales",
  "/dashboard/usuarios": "usuarios",
  "/dashboard/usuarios/nuevo": "usuarios",
  "/dashboard/usuarios/editar": "usuarios",
  "/dashboard/roles": "usuarios",
  "/dashboard/configuracion": "configuracion",
};

const TAB_TO_PATH = {
  micuenta: "/dashboard/micuenta",
  "gestionar-proyectos": "/dashboard/gestionar-proyectos",
  misproyectos: "/dashboard/misproyectos",
  "crear-proyecto": "/dashboard/crear-proyecto",
  socios: "/dashboard/socios",
  noticias: "/dashboard/noticias",
  cursos: "/dashboard/cursos",
  eventos: "/dashboard/eventos",
  contacto: "/dashboard/contacto",
  "contactos-inbox": "/dashboard/contactos-inbox",
  credenciales: "/dashboard/credenciales",
  usuarios: "/dashboard/usuarios",
  configuracion: "/dashboard/configuracion",
};

const DashboardLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // 🔐 Obtener usuario y función de logout del contexto
  const { user, token, logout } = useAuth();
  const routeTab = Object.keys(PATH_TO_TAB)
    .sort((a, b) => b.length - a.length)
    .find((pathPrefix) => location.pathname.startsWith(pathPrefix));
  const isRegistroSociosRoute = location.pathname.startsWith("/dashboard/registro-socios");
  const rawRole =
    user?.rol ||
    user?.rol_principal ||
    user?.rol_detalle ||
    user?.role ||
    (user?.roles && user.roles[0]?.nombre) ||
    (user?.roles_negocio && user.roles_negocio[0]?.rol?.nombre) ||
    "";
  const roleLabel = rawRole ? rawRole.toString().toUpperCase() : null;

  const permissions = useMemo(() => {
    const isAdmin = roleLabel === "ADMIN" || roleLabel === "ADMINISTRADOR";
    const isRevisor = roleLabel === "REVISOR";
    const isSocio = roleLabel === "SOCIO";
    const isAsistente = roleLabel === "ASISTENTE";
    const canEditAsistente = isAsistente && user?.is_staff;
    const sinRol = !roleLabel && !isAdmin && !user?.is_staff;
    const allowNoticiasCursosOnly = isAsistente && !isAdmin;
    const canSocios = isAdmin || ((isSocio || isRevisor) && user?.is_staff);
    const canMisProyectos = canSocios;
    const canCrearProyectos = isAdmin || isSocio || isRevisor;
    const canGestionarProyectos = isAdmin || isRevisor;

    return {
      isAdmin,
      isRevisor,
      isSocio,
      isAsistente,
      canEditAsistente,
      sinRol,
      allowNoticiasCursosOnly,
      canSocios,
      canMisProyectos,
      canCrearProyectos,
      canGestionarProyectos,
    };
  }, [roleLabel, user?.is_staff]);

  const getDefaultTab = useCallback(() => {
    if (permissions.allowNoticiasCursosOnly || permissions.sinRol) return "noticias";
    if (permissions.canGestionarProyectos) return "gestionar-proyectos";
    return "misproyectos";
  }, [permissions]);

  const canOpenTab = useCallback((tab) => {
    if (tab === "micuenta") return true;
    if (tab === "gestionar-proyectos")
      return !permissions.allowNoticiasCursosOnly && permissions.canGestionarProyectos;
    if (tab === "misproyectos")
      return !permissions.allowNoticiasCursosOnly && permissions.canMisProyectos;
    if (tab === "crear-proyecto")
      return !permissions.allowNoticiasCursosOnly && permissions.canCrearProyectos;
    if (tab === "socios") return !permissions.allowNoticiasCursosOnly && permissions.canSocios;
    if (tab === "noticias") return true;
    if (tab === "cursos") return true;
    if (tab === "eventos") return true;
    if (tab === "contacto") return true;
    if (tab === "contactos-inbox") return permissions.isAdmin || permissions.isAsistente;
    if (tab === "credenciales") return permissions.isAdmin || permissions.isAsistente;
    if (tab === "usuarios") return !permissions.allowNoticiasCursosOnly && permissions.isAdmin;
    if (tab === "configuracion") return permissions.isAdmin && user?.is_staff;
    return false;
  }, [permissions, user?.is_staff]);

  const [activeTab, setActiveTab] = useState(getDefaultTab);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    return localStorage.getItem("dashboardSidebarCollapsed") === "true";
  });

  useEffect(() => {
    localStorage.setItem("dashboardSidebarCollapsed", String(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  useEffect(() => {
    if (!routeTab) {
      setActiveTab((currentTab) => (canOpenTab(currentTab) ? currentTab : getDefaultTab()));
      return;
    }
    setActiveTab(canOpenTab(PATH_TO_TAB[routeTab]) ? PATH_TO_TAB[routeTab] : getDefaultTab());
  }, [routeTab, canOpenTab, getDefaultTab]);

  const navItems = [
    { tab: "gestionar-proyectos", label: "Gestionar Proyectos" },
    { tab: "misproyectos", label: "Mis Proyectos" },
    { tab: "crear-proyecto", label: "Crear Proyecto" },
    { tab: "socios", label: "Socios" },
    { tab: "noticias", label: "Noticias" },
    { tab: "cursos", label: "Cursos" },
    { tab: "eventos", label: "Eventos" },
    { tab: "contactos-inbox", label: "Mensajes de Contacto" },
    { tab: "credenciales", label: "Credenciales" },
    { tab: "usuarios", label: "Usuarios" },
    { tab: "configuracion", label: "Configuración del Sitio" },
  ].filter((item) => canOpenTab(item.tab));

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    navigate(TAB_TO_PATH[tab] || "/dashboard");
  };

  /**
   * 🚪 Manejo del logout
   */
  const handleLogout = async () => {
    // Intentar limpiar toda la información de autenticación (si es async)
    try {
      await (logout ? logout() : Promise.resolve());
      console.log("Sesión cerrada correctamente");
    } catch (err) {
      console.error("Error cerrando sesión:", err);
    }

    // Forzar redirección al home sin excepciones
    try {
      navigate("/", { replace: true });
    } catch (e) {
      // Ignorar cualquier fallo de navigate
    }

    // Fallback definitivo: recargar en / para garantizar que siempre llega al home
    try {
      if (typeof window !== "undefined") window.location.href = "/";
    } catch (e) {
      // nada más que hacer
    }
  };

  /**
   * 📝 Obtener el nombre del usuario para mostrar
   * Prioriza: nombre completo > username > email
   */
  const getDisplayName = () => {
    if (!user) return "Usuario";
    
    // Si tiene nombre y apellido
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    
    // Si solo tiene nombre
    if (user.first_name) {
      return user.first_name;
    }
    
    // Si tiene username
    if (user.username) {
      return user.username;
    }
    
    // Último recurso: email
    return user.email || "Usuario";
  };

  return (
    <div className={`dashboard-container ${isSidebarCollapsed ? "sidebar-collapsed" : ""}`}>
      {/* Sidebar lateral */}
      <aside className="sidebar">
        <h2>ABIS La Paz</h2>
        <ul>
          {navItems.map((item) => (
            <li key={item.tab}>
              <button
                type="button"
                className={`sidebar-nav-btn ${activeTab === item.tab ? "active" : ""}`}
                onClick={() => handleTabChange(item.tab)}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <button
        type="button"
        className="sidebar-toggle-btn"
        onClick={() => setIsSidebarCollapsed((prev) => !prev)}
        aria-label={isSidebarCollapsed ? "Mostrar menú lateral" : "Ocultar menú lateral"}
        title={isSidebarCollapsed ? "Mostrar menú lateral" : "Ocultar menú lateral"}
      >
        {isSidebarCollapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
        <span>{isSidebarCollapsed ? "Mostrar" : "Ocultar"}</span>
      </button>

      {/* Área principal */}
      <main className="main-content">
        <header className="dashboard-header">
          <h1>Panel Administrativo</h1>

          <div className="user-section">
            <div className="user-meta">
              <span className="user-name">{getDisplayName()}</span>
              <span className="user-role">{roleLabel || "SIN ROL ASIGNADO"}</span>
            </div>

            <IconButton
              aria-label="Mi cuenta"
              title="Mi Cuenta"
              onClick={() => handleTabChange("micuenta")}
              className={`account-icon-btn ${activeTab === "micuenta" ? "active" : ""}`}
              size="small"
            >
              <AccountCircleOutlinedIcon fontSize="medium" />
            </IconButton>

            <IconButton
              aria-label="Contacto"
              title="Contacto"
              onClick={() => handleTabChange("contacto")}
              className={`account-icon-btn ${activeTab === "contacto" ? "active" : ""}`}
              size="small"
            >
              <ContactPhoneOutlinedIcon fontSize="medium" />
            </IconButton>
            
            {/* 🚪 Botón de logout */}
            <button className="logout-btn" onClick={handleLogout}>
              Cerrar sesión
            </button>
          </div>
        </header>

        <MainDashboard
          token={token}
          user={user || {}}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          permissions={permissions}
          isRegistroSociosRoute={isRegistroSociosRoute}
        />
      </main>
    </div>
  );
};

export default DashboardLayout;
